import { Combobox, Dialog, Transition } from "@headlessui/react";
import { SearchIcon } from "@heroicons/react/solid";
import clsx from "clsx";
import { useRouter } from "next/router";
import { Fragment } from "react";
import { useEscapeKey } from "~/hooks/listeners";
import { useKeyDown } from "~/lib/useKeyDown";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { Avatar } from "../avatar";
import { useMemberSearchContainer } from "./containers/search";

export const SearchPopover = () => {
  const { isAdmin } = useCurrentUser();

  const { open, setOpen, search, setSearch, data, isLoading } =
    useMemberSearchContainer();
  const { push } = useRouter();

  const onHandleChange = ({ id }) => {
    setOpen(false);
    setSearch("");
    push(`/member/${id}`);
  };

  // cmd + K
  useKeyDown(
    75,
    (e) => {
      if (e.metaKey) {
        setOpen(true);
      }
    },
    []
  );

  //escape
  useEscapeKey(() => {
    setSearch("");
    setOpen(false);
  });

  if (!isAdmin) return null;

  return (
    <div className="relative inline-block text-left">
      <Transition.Root
        show={open}
        as={Fragment}
        afterLeave={() => setSearch("")}
      >
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto p-4 sm:p-6 md:p-20"
          onClose={setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-neutral-700 backdrop-blur backdrop-filter bg-opacity-25 transition-opacity" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Combobox
              as="div"
              className="mx-auto max-w-xl transform divide-y divide-neutral-800 border border-neutral-700/60 overflow-hidden rounded-xl bg-black backdrop-blur backdrop-filter shadow-2xl ring-1 ring-black ring-opacity-5 transition-all"
              onChange={onHandleChange}
            >
              <div className="relative">
                <SearchIcon
                  className="pointer-events-none absolute top-3.5 left-5 h-6 w-6 text-neutral-400"
                  aria-hidden="true"
                />
                <Combobox.Input
                  className="h-12 w-full border-0 bg-transparent pl-16 pr-4 text-white rounded-xl placeholder-neutral-400 focus:ring-0 focus:outline-0"
                  placeholder="Search..."
                  onChange={(event) => setSearch(event.target.value)}
                  value={search}
                />
              </div>

              {data.length > 0 && (
                <Combobox.Options
                  static
                  className="max-h-72 scroll-py-2 overflow-y-auto py-2 text-gray-100"
                >
                  {data.map((person) => (
                    <Combobox.Option
                      key={person.id}
                      value={person}
                      className={({ active }) =>
                        clsx(
                          "select-none px-4 py-1 flex gap-4 items-center cursor-pointer",
                          active && "bg-neutral-800 text-white"
                        )
                      }
                    >
                      <Avatar
                        src={person.profileImg + "?w=128"}
                        variant={Avatar.variant.Circle}
                        size={Avatar.size.XS}
                        fallbackText={`${person.givenName[0]} ${person.familyName[0]}`}
                      />
                      <div>
                        <span>{person.fullName}</span>
                        <span className="text-xs opacity-60 block">
                          {person.teamName || "Onboarding"}
                        </span>
                      </div>
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              )}

              {search !== "" && data.length === 0 && !isLoading && (
                <p className="p-4 text-sm text-gray-100">No people found.</p>
              )}
              {search !== "" && data.length === 0 && isLoading && (
                <p className="p-4 text-sm text-gray-100">Loading...</p>
              )}
            </Combobox>
          </Transition.Child>
        </Dialog>
      </Transition.Root>
    </div>
  );
};
